/**
* @file 数据分析-用户端行为分析
* */
<template>
  <div class="user-behavior-page">
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title"><i class="el-icon-s-custom"></i> 用户分析</h2>
        <span class="page-desc">分析用户行为、留存及活跃度数据</span>
      </div>
    </div>
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-radio-group v-model="timeType" size="small" @change="onTimeTypeChange">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadAll" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 概览卡片 -->
    <div class="section-title"><i class="el-icon-user"></i> 用户行为概览</div>
    <div class="overview-cards">
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#409EFF,#66B1FF)"><i class="el-icon-s-custom"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ metrics.totalUv | numFormat }}</div><div class="ov-card-label">总UV（独立会话）</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#67C23A,#85CE61)"><i class="el-icon-view"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ metrics.totalPv | numFormat }}</div><div class="ov-card-label">总PV（页面访问）</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#E6A23C,#EBB563)"><i class="el-icon-s-order"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ metrics.orderStartUv | numFormat }}</div><div class="ov-card-label">下单会话数</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#9B59B6,#B07CC6)"><i class="el-icon-success"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ metrics.paySuccessUv | numFormat }}</div><div class="ov-card-label">支付成功会话数</div></div>
      </div>
      <div class="ov-card">
        <div class="ov-card-icon" style="background:linear-gradient(135deg,#F56C6C,#F78989)"><i class="el-icon-data-analysis"></i></div>
        <div class="ov-card-info"><div class="ov-card-value">{{ metrics.conversionRate }}%</div><div class="ov-card-label">整体转化率</div></div>
      </div>
    </div>

    <!-- 图表 3列：UV/PV趋势 + 转化漏斗 + 设备分布 -->
    <div class="charts-grid">
      <div class="chart-card" style="grid-column:span 2">
        <div class="chart-card-header"><span><i class="el-icon-data-line"></i> 每日UV/PV趋势</span></div>
        <div style="padding:16px">
          <div v-if="dailyTrend.length === 0" class="empty-tip">暂无数据</div>
          <div v-else class="trend-chart">
            <div class="trend-legend">
              <span class="legend-dot blue"></span>UV（独立会话）
              <span class="legend-dot green" style="margin-left:16px"></span>PV（访问次数）
            </div>
            <div class="trend-bars">
              <div v-for="(item, i) in dailyTrend.slice(-14)" :key="i" class="trend-day">
                <el-tooltip :content="`UV: ${item.uv}  PV: ${item.pv}`" placement="top">
                  <div class="bar-group">
                    <div class="bar uv-bar" :style="{ height: getBarHeight(item.uv, 'uv') + 'px' }"></div>
                    <div class="bar pv-bar" :style="{ height: getBarHeight(item.pv, 'pv') + 'px' }"></div>
                  </div>
                </el-tooltip>
                <div class="day-label">{{ item.day && item.day.slice(5) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-s-data"></i> 下单转化漏斗</span></div>
        <div style="padding:16px">
          <div v-if="funnel.length === 0" class="empty-tip">暂无数据</div>
          <div v-else class="funnel-wrap">
            <div v-for="(step, i) in funnel" :key="i" class="funnel-step">
              <div class="funnel-bar-wrap">
                <div class="funnel-bar" :style="{ width: getFunnelWidth(step.count) + '%', background: funnelColors[i] }"></div>
              </div>
              <div class="funnel-info">
                <span class="funnel-label">{{ step.step }}</span>
                <span class="funnel-count">{{ step.count | numFormat }}</span>
                <span v-if="i > 0 && funnel[i-1].count > 0" class="funnel-rate">{{ (step.count / funnel[i-1].count * 100).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2列：城市分布 + 设备/网络分布 -->
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-place"></i> 用户城市分布 TOP15</span></div>
        <div style="padding:16px">
          <div v-if="cityList.length === 0" class="empty-tip">暂无数据</div>
          <div v-else>
            <div v-for="(item, i) in cityList" :key="i" class="city-item">
              <span class="city-rank" :class="i < 3 ? 'top' : ''">{{ i + 1 }}</span>
              <span class="city-name">{{ item.city || '未知' }}</span>
              <div class="city-bar-wrap"><div class="city-bar" :style="{ width: (item.cnt / cityList[0].cnt * 100) + '%' }"></div></div>
              <span class="city-cnt">{{ item.cnt }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-mobile-phone"></i> 设备 &amp; 网络分布</span></div>
        <div class="device-network-grid">
          <div class="device-section">
            <div class="dist-section-title">设备平台分布</div>
            <div v-if="platformList.length === 0" class="empty-tip">暂无数据</div>
            <div v-else class="pie-list">
              <div v-for="(item, i) in platformList" :key="i" class="pie-item">
                <div class="pie-color" :style="{ background: pieColors[i % pieColors.length] }"></div>
                <span class="pie-label">{{ item.platform || '未知' }}</span>
                <div class="pie-bar-wrap"><div class="pie-bar" :style="{ width: getPct(item.cnt, platformList) + '%', background: pieColors[i % pieColors.length] }"></div></div>
                <span class="pie-val">{{ item.cnt }}</span>
                <span class="pie-pct">{{ getPct(item.cnt, platformList) }}%</span>
              </div>
            </div>
          </div>
          <div class="device-divider"></div>
          <div class="network-section">
            <div class="dist-section-title">网络类型分布</div>
            <div v-if="networkList.length === 0" class="empty-tip">暂无数据</div>
            <div v-else class="pie-list">
              <div v-for="(item, i) in networkList" :key="i" class="pie-item">
                <div class="pie-color" :style="{ background: networkColors[item.network_type] || '#ccc' }"></div>
                <span class="pie-label">{{ item.network_type || 'unknown' }}</span>
                <div class="pie-bar-wrap"><div class="pie-bar" :style="{ width: getPct(item.cnt, networkList) + '%', background: networkColors[item.network_type] || '#ccc' }"></div></div>
                <span class="pie-val">{{ item.cnt }}</span>
                <span class="pie-pct">{{ getPct(item.cnt, networkList) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2列：TOP页面 + 事件统计 -->
    <div class="tables-grid">
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-tickets"></i> 热门页面 TOP20</span></div>
        <div style="padding:16px">
          <el-table :data="topPages" size="small" stripe border>
            <el-table-column label="页面路径" prop="page_path" min-width="200"></el-table-column>
            <el-table-column label="PV" prop="pv" width="80" sortable></el-table-column>
            <el-table-column label="UV" prop="uv" width="80" sortable></el-table-column>
            <el-table-column label="PV/UV" width="90">
              <template slot-scope="{ row }">{{ row.uv > 0 ? (row.pv / row.uv).toFixed(1) : '-' }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header"><span><i class="el-icon-odometer"></i> 用户行为事件统计</span></div>
        <div style="padding:16px">
          <el-table :data="eventTypeStats" size="small" stripe border>
            <el-table-column label="事件类型" prop="event_type" min-width="160"></el-table-column>
            <el-table-column label="次数" prop="cnt" width="100" sortable></el-table-column>
            <el-table-column label="说明" min-width="120">
              <template slot-scope="{ row }"><span style="font-size:12px;color:#909399">{{ getEventDesc(row.event_type) }}</span></template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const EVENT_DESC = {
  app_launch: '小程序启动',
  page_view: '页面访问',
  page_leave: '页面离开',
  click: '点击事件',
  search: '搜索行为',
  line_view: '浏览线路',
  shift_view: '查看班次',
  order_start: '开始下单',
  order_submit: '提交订单',
  order_pay_start: '发起支付',
  order_pay_success: '支付成功',
  order_pay_cancel: '取消支付',
  order_pay_fail: '支付失败',
  coupon_use: '使用优惠券',
  recommend_click: '推荐线路点击',
  banner_click: '广告点击',
  login_start: '开始登录',
  login_success: '登录成功',
  login_fail: '登录失败',
}

export default {
  name: 'userBehaviorAnalysis',
  filters: {
    numFormat(val) {
      if (val == null || val === '') return '0'
      return Number(val).toLocaleString()
    }
  },
  data() {
    return {
      loading: false,
      timeType: 0,
      metrics: { totalUv: 0, totalPv: 0, orderStartUv: 0, paySuccessUv: 0, conversionRate: '0.0' },
      dailyTrend: [],
      funnel: [],
      cityList: [],
      platformList: [],
      networkList: [],
      topPages: [],
      eventTypeStats: [],
      funnelColors: ['#409EFF','#5CB85C','#E6A23C','#F0944D','#F56C6C','#909399','#67C23A','#FF6633'],
      pieColors: ['#409EFF','#67C23A','#E6A23C','#F56C6C','#909399','#FF6633'],
      networkColors: { wifi: '#67C23A', '5g': '#409EFF', '4g': '#5CB85C', '3g': '#E6A23C', '2g': '#F56C6C', unknown: '#909399' },
    }
  },
  computed: {
    maxUv() { return Math.max(...this.dailyTrend.map(d => Number(d.uv) || 0), 1) },
    maxPv() { return Math.max(...this.dailyTrend.map(d => Number(d.pv) || 0), 1) },
    maxFunnel() { return this.funnel.length > 0 ? Math.max(...this.funnel.map(s => Number(s.count) || 0), 1) : 1 },
  },
  created() {
    this.loadAll()
  },
  methods: {
    onTimeTypeChange() {
      this.loadAll();
    },
    getTimeRange() {
      const today = new Date(); today.setHours(0,0,0,0)
      const todayStart = today.getTime()
      const todayEnd = new Date(); todayEnd.setHours(23,59,59,999)
      if (this.timeType === 0) return { startTime: todayStart, endTime: todayEnd.getTime() }
      if (this.timeType === 1) {
        const y = new Date(todayStart - 86400000)
        return { startTime: y.getTime(), endTime: todayStart - 1 }
      }
      if (this.timeType === 2) return { startTime: todayStart - 6 * 86400000, endTime: todayEnd.getTime() }
      if (this.timeType === 3) return { startTime: todayStart - 29 * 86400000, endTime: todayEnd.getTime() }
      if (this.timeType === 4) return { startTime: todayStart - 179 * 86400000, endTime: todayEnd.getTime() }
      return { startTime: todayStart - 364 * 86400000, endTime: todayEnd.getTime() }
    },
    async loadAll() {
      this.loading = true
      const { startTime, endTime } = this.getTimeRange()
      const url = '/api/v1/manage/behavior/analysis/overview'
      const { code, data } = await this.$axios.get(url, { startTime, endTime })
      if (code === 1 && data) {
        this.dailyTrend = data.dailyTrend || []
        this.funnel = data.funnel || []
        this.networkList = (data.networkDistribution || []).map(i => ({ ...i, cnt: Number(i.cnt) }))
        this.platformList = (data.platformDistribution || []).map(i => ({ ...i, cnt: Number(i.cnt) }))
        this.cityList = (data.cityDistribution || []).map(i => ({ ...i, cnt: Number(i.cnt) }))
        this.topPages = data.topPages || []
        this.eventTypeStats = data.eventTypeStats || []

        const uvRow = this.dailyTrend.reduce((s, d) => s + Number(d.uv || 0), 0)
        const pvRow = this.dailyTrend.reduce((s, d) => s + Number(d.pv || 0), 0)
        const orderStart = this.funnel.find(f => f.eventType === 'order_start')
        const paySuccess = this.funnel.find(f => f.eventType === 'order_pay_success')
        const totalSessions = (this.funnel.find(f => f.eventType === 'app_launch') || {}).count || 1
        this.metrics = {
          totalUv: uvRow,
          totalPv: pvRow,
          orderStartUv: orderStart ? orderStart.count : 0,
          paySuccessUv: paySuccess ? paySuccess.count : 0,
          conversionRate: totalSessions > 0 ? ((paySuccess ? paySuccess.count : 0) / totalSessions * 100).toFixed(1) : '0.0'
        }
      }
      this.loading = false
    },
    getBarHeight(val, type) {
      const max = type === 'uv' ? this.maxUv : this.maxPv
      return Math.max(4, Math.round((Number(val) || 0) / max * 100))
    },
    getFunnelWidth(cnt) {
      return Math.max(4, Math.round((Number(cnt) || 0) / this.maxFunnel * 100))
    },
    getPct(cnt, list) {
      const total = list.reduce((s, i) => s + (Number(i.cnt) || 0), 0)
      return total > 0 ? (cnt / total * 100).toFixed(1) : '0.0'
    },
    getEventDesc(type) {
      return EVENT_DESC[type] || type
    }
  }
}
</script>

<style scoped lang="less">
.user-behavior-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  .filter-bar {
    display: flex; align-items: center; justify-content: space-between;
    background: #fff; padding: 16px 20px; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 20px;
  }

  .section-title {
    font-size: 16px; font-weight: 600; color: #303133; margin-bottom: 16px;
    display: flex; align-items: center;
    i { margin-right: 8px; color: #409EFF; }
  }

  .overview-cards {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-bottom: 24px;

    .ov-card {
      background: #fff; border-radius: 10px; padding: 16px;
      display: flex; align-items: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: all 0.2s ease;
      &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
      .ov-card-icon {
        width: 40px; height: 40px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        margin-right: 12px; flex-shrink: 0;
        i { color: #fff; font-size: 18px; }
      }
      .ov-card-info {
        min-width: 0;
        .ov-card-value { font-size: 20px; font-weight: 700; color: #303133; }
        .ov-card-label { font-size: 12px; color: #909399; margin-top: 2px; }
      }
    }
  }

  .chart-card {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden; min-width: 0;
    .chart-card-header {
      padding: 14px 20px; border-bottom: 1px solid #f0f0f0;
      font-size: 14px; font-weight: 600; color: #303133;
      display: flex; align-items: center;
      i { margin-right: 6px; color: #409EFF; }
    }
  }

  .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
  .charts-grid-half { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
  .tables-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 24px; }

  .empty-tip {
    text-align: center;
    color: #c0c4cc;
    padding: 30px 0;
    font-size: 13px;
  }

  // 趋势图
  .trend-chart {
    .trend-legend {
      font-size: 12px;
      color: #606266;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      .legend-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        margin-right: 4px;
        &.blue { background: #409EFF; }
        &.green { background: #67C23A; }
      }
    }
    .trend-bars {
      display: flex;
      align-items: flex-end;
      gap: 4px;
      height: 110px;
      overflow-x: auto;
      padding-bottom: 20px;

      .trend-day {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 24px;

        .bar-group {
          display: flex;
          align-items: flex-end;
          gap: 2px;
          height: 90px;
          .bar {
            width: 8px;
            border-radius: 3px 3px 0 0;
            &.uv-bar { background: #409EFF; }
            &.pv-bar { background: #67C23A; }
          }
        }
        .day-label {
          font-size: 9px;
          color: #909399;
          margin-top: 2px;
          transform: rotate(-35deg);
          white-space: nowrap;
        }
      }
    }
  }

  // 漏斗图
  .funnel-wrap {
    .funnel-step {
      margin-bottom: 10px;
      .funnel-bar-wrap {
        height: 10px;
        background: #f5f5f5;
        border-radius: 5px;
        overflow: hidden;
        margin-bottom: 4px;
        .funnel-bar {
          height: 100%;
          border-radius: 5px;
          transition: width 0.5s ease;
        }
      }
      .funnel-info {
        display: flex;
        align-items: center;
        font-size: 12px;
        .funnel-label { flex: 1; color: #606266; }
        .funnel-count { color: #303133; font-weight: 600; margin-right: 8px; }
        .funnel-rate { color: #67C23A; font-size: 11px; }
      }
    }
  }

  // 城市分布
  .city-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 12px;
    .city-rank {
      width: 20px;
      text-align: center;
      color: #909399;
      &.top { color: #E6A23C; font-weight: 700; }
    }
    .city-name { width: 60px; color: #606266; margin: 0 8px; flex-shrink: 0; }
    .city-bar-wrap {
      flex: 1;
      height: 8px;
      background: #f0f0f0;
      border-radius: 4px;
      overflow: hidden;
      .city-bar {
        height: 100%;
        background: linear-gradient(90deg, #409EFF, #66B1FF);
        border-radius: 4px;
        transition: width 0.4s;
      }
    }
    .city-cnt { width: 40px; text-align: right; color: #303133; font-weight: 600; }
  }

  // 饼图列表
  .pie-list {
    .pie-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-size: 12px;
      .pie-color {
        width: 10px;
        height: 10px;
        border-radius: 2px;
        margin-right: 6px;
        flex-shrink: 0;
      }
      .pie-label { width: 60px; color: #606266; flex-shrink: 0; }
      .pie-bar-wrap {
        flex: 1; height: 8px; background: #f0f0f0;
        border-radius: 4px; overflow: hidden; margin: 0 8px;
        .pie-bar { height: 100%; border-radius: 4px; transition: width 0.4s; }
      }
      .pie-val { color: #303133; font-weight: 600; margin-right: 6px; width: 40px; text-align: right; }
      .pie-pct { color: #909399; width: 42px; text-align: right; }
    }
  }

  // 设备网络并排布局
  .device-network-grid {
    display: flex;
    padding: 16px;
    gap: 0;
    min-height: 200px;

    .device-section, .network-section {
      flex: 1;
      min-width: 0;
    }

    .device-divider {
      width: 1px;
      background: #f0f0f0;
      margin: 0 16px;
      flex-shrink: 0;
    }

    .dist-section-title {
      font-size: 13px;
      font-weight: 600;
      color: #606266;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px dashed #ebeef5;
    }
  }
}
</style>
